import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Types
import { AuthContextType } from '../../../../../../hooks/useAuth';

// GraphQL
import { client } from '../../../../../../gql/client';
import { selectAccidentById } from '../../../../../../gql/selectors/accidents';
import { FETCH_PREDEFINED_ORDERS } from '../../../../../../gql/queries/orders/predefinedData';
import debug from 'debug';

/**
 * Loader para cargar datos de accidente por ID directo del accidente
 *
 * A diferencia de LoaderGeneralViewAccidentSummary (que usa orderId),
 * este loader recibe el accidentId directamente.
 *
 * Uso: Ruta /work-orders/accidents/detail/:accidentId
 * Desde: Navegación en ShowOT (AccidentOT component)
 */

const loadAccidentSummaryById = async (
  accidentId: string,
  userData: AuthContextType | null
) => {
  const logLoader = debug('rois:loader:accident-summary-by-id');
  debug.enable('rois:loader:accident-summary-by-id');
  debug.enable('rois:selector:accident');
  logLoader(
    `🔍 Iniciando carga de resumen de accidente para accidentId: ${accidentId}`
  );

  try {
    logLoader(
      `👤 Usuario: ${userData?.user?.user?.name} ${userData?.user?.user?.surName}`
    );

    const startTime = performance.now();
    // Fetch accident data and predefinedOrders in parallel
    const [orderAccidentData, { data: { predefinedOrders } }] = await Promise.all([
      selectAccidentById(accidentId),
      client.query({ query: FETCH_PREDEFINED_ORDERS }),
    ]);
    const endTime = performance.now();

    logLoader(
      `⏱️ Selector completado en ${(endTime - startTime).toFixed(2)}ms`
    );
    logLoader(`📊 Datos obtenidos: ${orderAccidentData ? 'OK' : 'NULL'}`);

    if (orderAccidentData) {
      logLoader(
        `🚗 Vehículos en resultado: ${orderAccidentData.vehicles?.length || 0}`
      );
      logLoader(
        `🖼️ Imágenes en resultado: ${orderAccidentData.images?.length || 0}`
      );
      logLoader(`📋 Estado del accidente: ${orderAccidentData.state}`);
      logLoader(`📋 Predefine: ${orderAccidentData.predefine || 'N/A'}`);
      logLoader(`📋 PredefinedOrders cargados: ${predefinedOrders?.length || 0}`);
    }

    const result = {
      ...orderAccidentData,
      predefinedOrders, // ✅ FIX: Añadir predefinedOrders para mostrar en WorkOrder
      operator: `${userData?.user?.user?.name} ${
        userData?.user?.user?.surName.split(' ')[0]
      }`,
    };

    logLoader(`✅ loadAccidentSummaryById completado exitosamente`);
    return result;
  } catch (err) {
    logLoader(`❌ Error en loadAccidentSummaryById:`, err);
    Sentry.captureException(err);
    console.log('> Accident Summary By ID loading error:', err);
    return false;
  }
};

export const LoaderAccidentById: (
  userData: AuthContextType | null
) => LoaderFunction =
  (userData) =>
  async ({ params }) => {
    const logLoaderFunction = debug('rois:loader-function:accident-summary-by-id');
    logLoaderFunction(`🔍 LoaderFunction iniciado para params:`, params);

    if (!params.accidentId) {
      logLoaderFunction(`❌ No hay params.accidentId, retornando null`);
      return null;
    }

    const cacheSize = Object.keys(client.cache.extract()).length;
    logLoaderFunction(`📊 Tamaño de caché: ${cacheSize} entradas`);

    if (cacheSize === 0) {
      logLoaderFunction(`❌ Caché vacía, retornando null`);
      return null;
    }

    logLoaderFunction(`✅ Condiciones válidas, llamando a loadAccidentSummaryById`);
    const loaderToResolve = await loadAccidentSummaryById(params.accidentId, userData);

    logLoaderFunction(
      `📦 Resultado del loader: ${loaderToResolve ? 'OK' : 'NULL/FALSE'}`
    );
    const result = { data: loaderToResolve };
    logLoaderFunction(`🎯 Retornando defer con data`);

    return result;
  };
